import quicklookModalUtils from 'utils/Quicklook';
import skuUtils from 'utils/Sku';
import anaUtils from 'analytics/utils';
import constants from 'constants/content';

const { ACTION_TYPES } = constants;

/**
 * Resolves the Quick Look target for a CMS action, driven purely by its
 * targetUrl. An `ActionLinkExternal` action opens Quick Look in place (instead
 * of navigating) when its targetUrl carries `quicklook=true`, e.g.
 * `/product/gift-wrap-P370321?quicklook=true`. The product id is the last
 * dash-delimited segment of the targetUrl path (Sephora URL convention). The
 * sku id is OPTIONAL and read from the `skuId` query param when present
 * (`?quicklook=true&skuId=1169945`); when absent, Quick Look opens the product's
 * default sku. Returns `null` when the action doesn't opt in or the product id
 * can't be parsed, so callers fall back to normal (CXS action) navigation.
 * (OMNX-11286)
 *
 * @param  {?Object} action - CXS Action shape ({ type, targetUrl, ... }).
 * @return {?{productId: string, skuId: (string|undefined)}}
 */
export const getQuickLookDeeplinkParams = action => {
    if (!action || action.type !== ACTION_TYPES.EXTERNAL) {
        return null;
    }

    const targetUrl = action.targetUrl || '';

    if (anaUtils.getQueryParam('quicklook', targetUrl) !== 'true') {
        return null;
    }

    const [path] = targetUrl.split('?');
    // Normalize to uppercase to match the codebase's URL->productId convention
    // (e.g. AddToBasketActions) so a lowercase `...-p370321` still dispatches an
    // uppercase product id.
    const productId = path?.split('-').pop()?.toUpperCase();

    if (!productId || !/^P\d+$/.test(productId)) {
        return null;
    }

    const skuId = anaUtils.getQueryParam('skuId', targetUrl) || undefined;

    return { productId, skuId };
};

/**
 * Opens the Quick Look modal for a targetUrl-driven deeplink, suppressing the
 * CXS action (no navigation). `dispatchQuicklook` fetches the product itself, so
 * only productId is required; when skuId is omitted, Quick Look uses the
 * product's default sku.
 *
 * @param {{productId: string, skuId: (string|undefined)}} params
 */
export const openQuickLookDeeplink = ({ productId, skuId } = {}) => {
    if (!productId) {
        return;
    }

    quicklookModalUtils.dispatchQuicklook({
        productId,
        sku: skuId ? { skuId } : {},
        skuType: skuUtils.skuTypes.STANDARD,
        options: { addCurrentSkuToProductChildSkus: true }
    });
};
